import { useMemo, useState } from "react";

import { UserPlus, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ColorDot } from "@/components/cinch/ColorDot";
import { useCan } from "@/lib/auth/useCan";

import {
  useInviteParticipant,
  useOptions,
  useRemoveParticipant,
  useUpdateParticipant,
  type PlanningSession,
} from "../api";
import { PARTICIPANT_COLORS } from "../constants";

/** Who's in the session and what color they write in. Anyone in the session
 *  can look; only session managers (planning.write) can invite, recolor, or
 *  remove. */
export function ParticipantsDialog({
  session,
  open,
  onOpenChange,
}: {
  session: PlanningSession;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const canWrite = useCan("planning.write");
  const canAdmin = useCan("planning.admin");
  const canManage = canWrite || canAdmin;
  const options = useOptions();
  const invite = useInviteParticipant(session.id);
  const updateParticipant = useUpdateParticipant();
  const removeParticipant = useRemoveParticipant();
  const [pickUser, setPickUser] = useState("");

  const memberIds = useMemo(
    () => new Set(session.participants.map((p) => p.user_id)),
    [session.participants],
  );
  const invitable = (options.data?.users ?? []).filter((u) => !memberIds.has(u.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Participants</DialogTitle>
          <DialogDescription>
            Invited people see this session and post in their color. Everyone
            else only sees it with planning read access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {session.participants.map((p) => (
            <div key={p.id} className="flex items-center gap-2 rounded-md border px-2.5 py-1.5">
              <ColorDot color={p.color} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{p.display_name}</div>
                <div className="truncate text-[11px] text-muted-foreground">{p.email}</div>
              </div>
              {canManage ? (
                <>
                  <div className="flex flex-wrap gap-1">
                    {PARTICIPANT_COLORS.slice(0, 8).map((c) => (
                      <button
                        key={c}
                        type="button"
                        title={c}
                        onClick={() =>
                          updateParticipant.mutate(
                            { id: p.id, color: c },
                            { onError: (err) => toast.error(err.message) },
                          )
                        }
                      >
                        <ColorDot color={c} size="md" selected={p.color === c} />
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      removeParticipant.mutate(p.id, {
                        onSuccess: () => toast.success(`${p.display_name} removed`),
                        onError: (err) => toast.error(err.message),
                      })
                    }
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : null}
            </div>
          ))}
        </div>

        {canManage ? (
          <div className="flex items-center gap-2">
            <Select value={pickUser} onValueChange={setPickUser}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Add someone…" />
              </SelectTrigger>
              <SelectContent>
                {invitable.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.display_name} · {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              disabled={!pickUser || invite.isPending}
              onClick={() =>
                invite.mutate(
                  { user_id: pickUser, color: null },
                  {
                    onSuccess: (p) => {
                      setPickUser("");
                      toast.success(`${p.display_name} invited`);
                    },
                    onError: (err) => toast.error(err.message),
                  },
                )
              }
            >
              <UserPlus className="mr-1 h-3.5 w-3.5" /> Invite
            </Button>
          </div>
        ) : null}
        {canManage && invitable.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Everyone who has signed in to Cinch Ops is already in this session.
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
